import { createContext, useContext } from 'react';

import type { components } from '@heidi/schema/ml-scribe';
import type { CountryType } from '@heidi/utils/countries';
import type { PersonalizationStep } from '../../../contracts';

import type { UsePersonalizationFormReturn } from '../hooks/usePersonalizationForm';
import type {
  DetailLevel,
  EditActivity,
  MicTestPhase,
  PreviewTab,
} from '../learnBasicsTypes';

/** The onboarding analytics port; the app's `useOnboardingAnalytics()` return satisfies it. */
export interface OnboardingAnalytics {
  trackOnboardingStarted: (params: { source?: string; region?: string }) => void;
  trackStepCompleted: (
    step: PersonalizationStep,
    stepNumber: number,
    options?: { phoneNumberValid?: boolean }
  ) => void;
  trackStepSkipped: (
    stepName: PersonalizationStep,
    options?: { tasksCompletedCount?: number }
  ) => void;
  trackTemplateStarted: (params: { usedSuggestion: boolean; specialty?: string }) => void;
  trackTemplateCompleted: () => void;
  trackTemplateReviewed: (madeChanges: boolean) => void;
  trackDocumentsSelected: (params: { count: number; docTypes: string[] }) => void;
  trackOnboardingCompleted: () => void;
  resetStepTimer: () => void;
  trackNoteStyleUpdated: (abbreviationsEnabled: boolean) => void;
  trackEvidenceQueried: (params: {
    queryType: 'suggested' | 'custom';
    queryLength: number;
  }) => void;
  trackEvidenceAccepted: (itemsCount: number) => void;
  trackEvidenceDiscarded: (itemsCount: number) => void;
  trackDocumentCreated: (documentType: string) => void;
  trackLearnBasicsCompleted: (tasksCompleted: string[]) => void;
  trackLearnBasicsSkipped: (tasksCompletedCount: number) => void;
}

export interface OnboardingTemplateState {
  generatedTemplateDisplayName: string;
  generatedTemplateDisplayContent: string;
  createdTemplateName: string | undefined;
  isEditingTemplate: boolean;
  setIsEditingTemplate: (value: boolean) => void;
  isGenerating: boolean;
  isEditing: boolean;
  handleCreateTemplate: () => void;
  handleSkipTemplate: () => void;
  handleSaveAndContinue: () => void;
  handleApplyChanges: (changeRequest: string) => void;
  handleUndoLastTemplateEdit: () => void;
  canUndoLastTemplateEdit: boolean;
  handleTemplateNameChange: (name: string) => void;
  handleTemplateContentChange: (content: string) => void;
}

export interface OnboardingDocumentState {
  documentTemplates: components['schemas']['TemplateResponseV2'][];
  selectedDocumentIds: Set<string>;
  selectedTemplates: components['schemas']['TemplateResponseV2'][];
  isLoadingDocuments: boolean;
  hasDocumentSelectionError: boolean;
  toggleDocumentSelection: (templateId: string) => void;
  submitFavorites: (additionalFavoriteIds?: string[]) => Promise<void>;
}

export interface OnboardingCompletionState {
  /** `false` means the flow already exited (or failed). */
  completeOnboarding: () => Promise<boolean>;
  /** Privacy/completion CTA — runs completeOnboarding (PATCH + deferred invites). */
  onGetStarted: () => void;
  isCompletingOnboarding: boolean;
  onboardingError: Error | null;
}

export interface OnboardingMicTestState {
  transcript: string;
  setTranscript: (value: string) => void;
  phase: MicTestPhase;
  setPhase: (phase: MicTestPhase) => void;
  editActivity: EditActivity;
  setEditActivity: (activity: EditActivity) => void;
  recordingDurationSec: number;
  setRecordingDurationSec: (value: number) => void;
  /**
   * Sticky flag set the first time the user advances past the mic-test step.
   * Survives Back navigation so the Continue button isn't re-gated when they
   * return to mic-test; the recording-required gate is a one-time onboarding
   * affordance, not a permanent constraint.
   */
  hasCompleted: boolean;
  setHasCompleted: (value: boolean) => void;
  /**
   * Shared state for the stepped Learn-the-basics flow (sample-conversation
   * flag ON). The left rail owns the step controls; the preview reacts:
   *   - `detailLevel` drives the note verbosity transform.
   *   - `evidenceRequested` / `documentRequested` open the Evidence / Document
   *     tabs in the preview when the user acts on the left.
   */
  detailLevel: DetailLevel;
  setDetailLevel: (value: DetailLevel) => void;
  evidenceRequested: boolean;
  setEvidenceRequested: (value: boolean) => void;
  /**
   * Index (0-based) of the suggested evidence question the user last tapped.
   * Changing it remounts the preview's evidence response so the loading →
   * answer animation replays for the newly selected query.
   */
  evidenceQueryIndex: number;
  setEvidenceQueryIndex: (value: number) => void;
  /**
   * Document option indexes the user has checked on the Document step (in
   * selection order). Each opens its own Document tab in the preview;
   * unchecking removes its tab. `createdDocument` completes while ≥ 1 selected.
   */
  selectedDocuments: number[];
  setSelectedDocuments: (value: number[]) => void;
  /** Active preview tab, shared so the left rail and preview stay in sync. */
  noteTab: PreviewTab;
  setNoteTab: (value: PreviewTab) => void;
}

/**
 * Shared onboarding state for personalization flow. Grouped into sub-objects
 * so consumers destructure only the slice they need:
 *   const { template, formData } = useOnboardingContext();
 */
export type OnboardingContextValue = UsePersonalizationFormReturn & {
  analytics: OnboardingAnalytics;
  template: OnboardingTemplateState;
  documents: OnboardingDocumentState;
  completion: OnboardingCompletionState;
  micTest: OnboardingMicTestState;
  userEmail: string | undefined;
  handleCountryChange: (
    value: CountryType | null,
    options?: { isSeed?: boolean }
  ) => void;
  handleBack: () => void;
  handleContinue: () => void;
  submitAndAdvance: (trackAsSkip: boolean) => void;
  onMicSkip: () => void;
  /**
   * Lets the note-edit sub-flow intercept the header Back button. The handler
   * returns `true` if it consumed the back press (e.g. stepped back through an
   * internal sub-step); `false` (or unregistered) lets the default back run.
   */
  registerNoteEditBackHandler: (handler: (() => boolean) | null) => void;
};

const OnboardingContext = createContext<OnboardingContextValue | null>(null);

export function useOnboardingContext(): OnboardingContextValue {
  const ctx = useContext(OnboardingContext);
  if (!ctx) {
    throw new Error('useOnboardingContext must be used within OnboardingProvider');
  }
  return ctx;
}

export function useOptionalOnboardingContext(): OnboardingContextValue | null {
  return useContext(OnboardingContext);
}

export { OnboardingContext };
